'use client';

import InlineStyle from '@/components/InlineStyle';
import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { syncComplianzFromLbConsent } from '@/lib/complianzConsentBridge';
const COOKIE_KEY = 'lb_consent';
const SPLASH_KEY = 'lb_splash_seen';
function updateConsent(granted) {
  if (typeof window === 'undefined' || !window.gtag) return;
  const val = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: val,
    ad_user_data: val,
    ad_personalization: val,
    analytics_storage: val
  });
}
export default function CookieBanner() {
  const {
    t
  } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    /* Evaluate whether to show the cookie banner.
       For first-time visitors the IntroSplash is showing on top — we
       MUST wait until that splash dismisses before this banner mounts,
       otherwise both stack at the same z-index and ruin the intro. */
    const evaluate = () => {
      try {
        const stored = localStorage.getItem(COOKIE_KEY);
        if (!stored) setVisible(true);
        else {
          updateConsent(stored === 'accepted');
          syncComplianzFromLbConsent(stored === 'accepted');
        }
      } catch {}
    };
    if (typeof window === 'undefined') return;
    const splashAlreadyDismissed = window.sessionStorage.getItem(SPLASH_KEY) === '1';
    if (splashAlreadyDismissed) {
      evaluate();
      return;
    }

    /* Splash still up — wait for its dismissal event. The fallback timer
       covers the case where the IntroSplash is disabled (so the event never
       fires) — without it the banner would never appear for new visitors. */
    const onSplashDismissed = () => { clearTimeout(fallback); evaluate(); };
    window.addEventListener('lb:splash-dismissed', onSplashDismissed, { once: true });
    const fallback = setTimeout(() => {
      window.removeEventListener('lb:splash-dismissed', onSplashDismissed);
      evaluate();
    }, 1500);
    return () => {
      clearTimeout(fallback);
      window.removeEventListener('lb:splash-dismissed', onSplashDismissed);
    };
  }, []);
  function handle(accepted) {
    setSaving(true);
    try {
      localStorage.setItem(COOKIE_KEY, accepted ? 'accepted' : 'declined');
    } catch {}
    updateConsent(accepted);
    syncComplianzFromLbConsent(accepted);
    setTimeout(() => setVisible(false), 180);
  }
  if (!visible) return null;
  const linkStyle = {
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: "'Inter Tight',sans-serif",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'underline',
    textUnderlineOffset: 3
  };
  return <div role="dialog" aria-label={t("cookies.cookie_consent")} style={{
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 9999,
    width: 'min(320px, calc(100vw - 32px))',
    background: '#fff',
    border: '1px solid #E8ECF4',
    borderRadius: 14,
    padding: '14px 16px',
    boxShadow: '0 12px 32px rgba(15,23,42,0.12)',
    fontFamily: "'Inter Tight',sans-serif",
    animation: 'lb-cookie-in 0.3s cubic-bezier(0.16,1,0.3,1)'
  }}>
      <InlineStyle css={`
        @keyframes lb-cookie-in {
          from { opacity:0; transform:translateY(12px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `} />
      <p style={{
      margin: 0,
      fontSize: 13,
      lineHeight: 1.55,
      color: '#0D1117'
    }}>
        {t('cookies.message')}
        {' '}
        <a href="/privacy-policy/" style={{
        color: '#4F6EF7',
        textDecoration: 'underline',
        textUnderlineOffset: 3
      }}>
          {t('cookies.privacy')}
        </a>
      </p>
      <div style={{
      display: 'flex',
      gap: 18,
      marginTop: 10
    }}>
        <button onClick={() => handle(true)} disabled={saving} style={{
        ...linkStyle,
        color: '#4F6EF7'
      }}>
          {t('cookies.accept')}
        </button>
        <button onClick={() => handle(false)} disabled={saving} style={{
        ...linkStyle,
        color: '#6B7280'
      }}>
          {t('cookies.decline')}
        </button>
      </div>
    </div>;
}